/**
 * Utility function to calculate axis scale for charts.
 * It determines the maximum value for the axis and the number of ticks based on the provided buffer size and multiplier.
 * @param {number} maxValue - The maximum value for the axis.
 * @param {number} bufferSize - The buffer size to be added to the maximum value.
 * @param {number} multiplier - The multiplier for rounding the axis maximum.
 * @return {Object} - An object containing the rounded axis maximum and the tick amount.
 * @example
 * const { axisMax, tickAmount } = getAxisScale(100);
 * // axisMax will be 105 and tickAmount will be 5.
 * @example
 * const { axisMax, tickAmount } = getAxisScale(200, 0.2, 10);
 * // axisMax will be 220 and tickAmount will be 10.
 */
export const getAxisScale = (
  maxValue: number,
  bufferSize: number = 0.1,
  multiplier: number = 5
): { axisMax: number; tickAmount: number } => {
  const buffer = Math.ceil(maxValue * bufferSize);

  const axisMax = maxValue + buffer;

  // round axisMax 1 to 5, 48 to 50, 102 to 105, etc.
  const roundedAxisMax = Math.ceil(axisMax / multiplier) * multiplier;

  const tickAmount = Math.min(axisMax, multiplier);

  return { axisMax: roundedAxisMax, tickAmount };
};
